import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    IconButton,
    Avatar,
    Chip,
    Stack,
} from '@mui/material';
import { FC, ReactNode, MouseEvent } from 'react';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaidIcon from '@mui/icons-material/MonetizationOn';
import {
    currencyFormatter,
    formatDateSlashed,
    DescriptionCode,
    DESCRIPTION_CODE_LABEL,
    PaymentDirection,
} from '@lendica/utils';
import { mapCodeToLabel } from '../utils';
import StatusBadge from './StatusBadge';

export interface HistoryListItemProps {
    dealId: string;
    amount: number;
    product: string;
    date?: string;
    paymentDirection: PaymentDirection;
    transactionCategory: DescriptionCode;
    rate?: number;
    action?: boolean;
    status: string;
    hasNavigation?: boolean;
    onNavigationClick?(e: MouseEvent<HTMLElement>): void;
}

export const HistoryListItem: FC<HistoryListItemProps> = ({
    dealId,
    amount,
    product,
    date,
    paymentDirection,
    transactionCategory,
    status,
    action = false,
    hasNavigation = false,
    onNavigationClick,
}) => {
    let icon = paymentDirection === 'To Customer' ? <PaidIcon /> : <CreditCardIcon />;

    const signedAmount = (paymentDirection: PaymentDirection, amount: number) => {
        return paymentDirection === 'To Customer' ? +Math.abs(amount) : -Math.abs(amount);
    };

    let label = mapCodeToLabel(transactionCategory, paymentDirection);

    return (
        <ListItem
            button
            component="a"
            secondaryAction={
                action ? (
                    <IconButton edge="end">
                        <ArrowForwardIosIcon />
                    </IconButton>
                ) : null
            }
            disableGutters
            divider
            onClick={onNavigationClick}
        >
            {
                <ListItemAvatar>
                    <Avatar>{icon}</Avatar>
                </ListItemAvatar>
            }

            <Box flex="1" display="flex" flexDirection="column" gap={0.25}>
                <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                    <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ fontWeight: 400, fontSize: '14px', marginLeft: '0.25rem' }}
                    >
                        {label}
                    </Typography>
                </Box>

                {date && (
                    <Stack direction="row" spacing={1}>
                        <Chip size="small" key={'id'} label={dealId} />
                        <Chip
                            size="small"
                            key={'date'}
                            label={formatDateSlashed(date, true)}
                            color="primary"
                            variant="outlined"
                        />
                    </Stack>
                )}
            </Box>

            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.25}>
                <Typography variant="body2" color="text.primary">
                    {currencyFormatter(signedAmount(paymentDirection, amount))}
                </Typography>
                <StatusBadge status={status} />
            </Box>

            {hasNavigation && (
                <IconButton size="large" onClick={onNavigationClick} disableRipple>
                    <ArrowForwardIosIcon />
                </IconButton>
            )}
        </ListItem>
    );
};
